/**
 * TechnologyAssetsTable Component
 *
 * Enhanced table for displaying assets impacted by a technology with:
 * - Status column (first position, matches vulnerability drawer pattern)
 * - Asset column (combines IP + Domain for readability)
 * - Port and Service columns (critical for technology context)
 * - Filter dropdowns (status, attack surface, protocol, port)
 * - Search bar
 * - Bulk selection and actions
 *
 * This component uses the specialized useQueryTechnologyAssets hook for
 * filter management and maintains port-specific asset relationships.
 */

import React, { useCallback, useMemo, useState } from 'react';

import { DashedTag } from '@/components/chariot-ui';
import { SoundRecognitionSearch as SoundRecognitionSearchIcon } from '@/components/chariot-ui';
import { createAssetExportConfig } from '@/components/export/configs/asset.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';
import { NoteModal } from '@/components/modals';
import {
  buildAnnotateAction,
  buildExportAction,
  TableActionsBar,
} from '@/components/table/actions';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import {
  getColumnWidth,
  isColumnSortable,
} from '@/components/table/columns/registry';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { Skeleton } from '@/components/ui/skeleton';
import { Filters } from '@/hooks/useAssets';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import type { BulkActionPayload } from '@/hooks/useBulkSelection/types';
import { useAddComment } from '@/hooks/useComment';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { queryClient } from '@/queryclient';
import { AssetTableFilters } from '@/sections/asset/components/AssetTableFilters';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { AssetRemoveTag as RemoveTagModal } from '@/sections/asset/components/modals/RemoveTagModal';
import { AssetTag as TagModal } from '@/sections/asset/components/modals/TagModal';
import { TechnologyAssetCell } from '@/sections/technology/components/cells';
import {
  ImpactedAssetPort,
  useQueryTechnologyAssets,
} from '@/sections/technology/hooks/drawer';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';

/**
 * Props for TechnologyAssetsTable component
 */
export interface TechnologyAssetsTableProps {
  /** Technology key to filter assets by */
  technologyKey: string;
  /** Limit visible filters in drawer context */
  drawerFilters?: string[];
}

/**
 * Visible filters for the technology assets table
 * These filters are shown using the shared AssetTableFilters component
 */
const VISIBLE_FILTERS = ['status', 'attackSurface', 'protocol', 'port'];

/**
 * TechnologyAssetsTable Component
 *
 * Displays assets impacted by a technology with filtering, search, and bulk actions.
 * Preserves Port and Service columns which are critical for showing which
 * port/service the technology runs on.
 */
export function TechnologyAssetsTable({
  technologyKey,
  drawerFilters = ['status', 'attackSurface', 'protocol', 'port'],
}: TechnologyAssetsTableProps) {
  const { drawerState } = useGlobalState();
  const { isOsint } = useOsintContext();

  // Data fetching with filter management
  const {
    data: impactedAssetPorts,
    filters,
    setFilters,
    resetFilters,
    status,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    invalidate,
  } = useQueryTechnologyAssets({
    technologyKey,
    enabled: Boolean(technologyKey),
  });

  // Selection state.
  // selectedKeys holds each selected ROW's composite key (`${asset.key}#${port}`)
  // — that is the Table's selection identity, NOT the operation identity.
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [prevTechnologyKey, setPrevTechnologyKey] = useState(technologyKey);

  // Rows corresponding to the current selection. Matched on the composite row
  // key (item.key) — the same identity the Table stores — never asset.key.
  // selectedKeys survives a same-technology refetch (see the render-phase reset
  // below), so if a refetch drops rows this deliberately re-derives against the
  // currently-loaded set: it is the actionable, loaded-matched selection. Note
  // this is one entry per selected ROW (asset×port), not per asset — the
  // selection bar counts assets (selectedAssetKeys), not rows, so its "assets"
  // label matches the note/tag/untag/export target (ENG-5121, round 4).
  const selectedAssetPorts = useMemo(() => {
    // O(1) membership test — selectedKeys can reach hundreds in this drawer,
    // and impactedAssetPorts can be large for a popular technology, so avoid
    // the O(N*K) Array.includes inside the filter (ENG-5121 review, round 5).
    const keySet = new Set(selectedKeys);
    return impactedAssetPorts.filter(item => keySet.has(item.key));
  }, [impactedAssetPorts, selectedKeys]);

  // Deduped assets for operations. A single asset can occupy multiple ports, so
  // selectedAssetPorts may reference the same asset more than once — collapse to
  // unique assets (by asset.key) so note/tag/untag/export act on each asset once.
  const selectedAssets = useMemo(() => {
    const seen = new Set<string>();
    const assets: ImpactedAssetPort['asset'][] = [];
    for (const { asset } of selectedAssetPorts) {
      if (!seen.has(asset.key)) {
        seen.add(asset.key);
        assets.push(asset);
      }
    }
    return assets;
  }, [selectedAssetPorts]);

  // Real, deduped asset keys — the identity every downstream operation must use
  // (export `items`, tag/untag payloads, bulk selection). This is what ENG-5121
  // fixes: operations previously received the composite/synthetic row keys,
  // which matched nothing server-side and produced empty exports.
  const selectedAssetKeys = useMemo(
    () => selectedAssets.map(asset => asset.key),
    [selectedAssets]
  );

  // Deduped count of unique assets among the loaded rows — the honest
  // denominator for the asset-unit "N of M assets" selection bar (a single
  // asset can occupy multiple port rows). Kept in step with selectedAssetKeys,
  // which counts unique selected assets (ENG-5121, round 4).
  const totalAssetCount = useMemo(() => {
    const seen = new Set<string>();
    for (const { asset } of impactedAssetPorts) seen.add(asset.key);
    return seen.size;
  }, [impactedAssetPorts]);

  // Modal state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Create export config (no source options needed for drawer context)
  const assetExportConfig = useMemo(() => createAssetExportConfig([]), []);

  // Get visible filters - use drawerFilters if provided, otherwise default
  const visibleFilters = useMemo(
    () =>
      drawerFilters.filter(f => VISIBLE_FILTERS.includes(f)) as Array<
        keyof NonNullable<Filters['advancedFilters']>
      >,
    [drawerFilters]
  );

  // Detect if any filters are active (for empty state differentiation)
  const hasActiveFilters = useHasActiveFilters(filters);

  // Clear all filters callback
  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  // Build local selectionState from the deduped asset keys.
  // INVARIANT: assetKeys passed to RemoveTagModal is always selectedAssetKeys
  // (same source) — real asset keys, so bulk untag targets the right entities.
  const selectionState = useMemo(
    () => ({ mode: 'manual' as const, keys: selectedAssetKeys }),
    [selectedAssetKeys]
  );

  // Minimal bulkSelection adapter for useBulkConfirmation.
  // The drawer table uses raw selection (not useBulkSelection), so we provide a
  // lightweight adapter that satisfies the toBulkPayload contract. The bulk
  // endpoint keys on assets, so ship the deduped asset keys — never row keys.
  const drawerBulkSelection = useMemo(
    () => ({
      toBulkPayload: (): BulkActionPayload['selection'] => ({
        mode: 'explicit' as const,
        keys: selectedAssetKeys,
      }),
    }),
    [selectedAssetKeys]
  );

  // Bulk confirmation for untag. selectedKeys can reach hundreds in the drawer
  // table (all assets for a technology), so bulk path is warranted.
  const untagConfirm = useBulkConfirmation({
    resource: 'asset',
    action: 'untag',
    bulkSelection: drawerBulkSelection,
    clearSelection,
  });

  // Reset row selection when the drawer is reused for a different technology.
  // The drawer is intentionally NOT remounted across technology navigation
  // (drawers/index.tsx keys on index, not technologyKey — 2024-12-09), so
  // without this the previous technology's selection would carry into the new
  // table. Sanctioned "adjust state during render" — NOT a useEffect
  // (react-hooks/set-state-in-effect is an ESLint error under React Compiler).
  // A refetch of the SAME technology does not change technologyKey, so live
  // selection legitimately survives a refetch. Placed after untagConfirm so the
  // zero-selection block below can reference it (untagConfirm is declared here).
  if (prevTechnologyKey !== technologyKey) {
    setPrevTechnologyKey(technologyKey);
    setSelectedKeys([]);
  }

  // Keep modal-open state in sync with the live selection. The export gate
  // (open={isExportModalOpen && selectedAssetKeys.length > 0}) only *masks* the
  // modal when a background refetch empties the selection — it leaves the open
  // flag true, so the modal would silently reopen once the rows return or the
  // user picks a new row. Reset the flags so the close is authoritative; the
  // note/tag/untag modals get the same treatment because a zero-selection
  // operation would target no assets. This is React's sanctioned "adjust state
  // during render" alternative to an effect — each branch is guarded by the
  // flag it clears, so it fires once and cannot loop (setting a flag already
  // false bails out), and it commits before paint with no cascading render.
  if (selectedAssetKeys.length === 0) {
    if (isExportModalOpen) setIsExportModalOpen(false);
    if (isNoteModalOpen) setIsNoteModalOpen(false);
    if (isTagModalOpen) setIsTagModalOpen(false);
    if (isRemoveTagModalOpen) setIsRemoveTagModalOpen(false);
    if (untagConfirm.confirmPayload !== null && !untagConfirm.isPending) {
      untagConfirm.closeConfirmation();
    }
  }

  // Add comment mutation
  const { mutateAsync: addComment } = useAddComment();

  // Handle note submission
  const handleNoteSubmit = useCallback(
    async (note: string) => {
      const promises = selectedAssets.map(asset =>
        addComment({
          key: asset.key,
          name: asset.identifier || asset.key,
          comment: note,
          showSnackbar: false,
        })
      );

      await Promise.all(promises);

      // Invalidate queries
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'active',
      });

      setIsNoteModalOpen(false);
      clearSelection();
    },
    [selectedAssets, addComment, clearSelection]
  );

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setFilters(prev => ({
        ...prev,
        search: value,
      }));
    },
    [setFilters]
  );

  // Handle tag modal success
  const handleTagSuccess = useCallback(() => {
    invalidate();
    clearSelection();
  }, [invalidate, clearSelection]);

  // Handle row click - open asset drawer (disabled in OSINT mode)
  const handleRowClickFn = useCallback(
    ({ asset }: ImpactedAssetPort) => {
      drawerState.handleOpenAssetDrawer(asset);
    },
    [drawerState]
  );
  const handleRowClick = isOsint ? undefined : handleRowClickFn;

  // Dynamic noData config based on filter state
  const noDataConfig = useMemo(() => {
    const icon = (
      <SoundRecognitionSearchIcon
        className={`${NO_DATA_ICON_CLASS} text-[100px]`}
      />
    );

    // If filters are active and no results, show filtered empty state
    if (hasActiveFilters && impactedAssetPorts.length === 0) {
      return createFilteredEmptyConfig({
        entityName: 'assets',
        icon,
        hasActiveFilters,
        onClearFilters: handleClearFilters,
      });
    }

    // Otherwise show the default empty state
    return createAllEmptyConfig({
      entityName: 'No assets impacted by this technology',
      icon,
    });
  }, [hasActiveFilters, impactedAssetPorts.length, handleClearFilters]);

  return (
    <div className="flex h-full flex-col">
      <TableControlsContainer
        data-testid="technology-assets-controls"
        filters={
          // Note: ClearFiltersButton is already rendered inside AssetTableFilters
          <AssetTableFilters
            filters={filters as Filters}
            setFilters={
              setFilters as React.Dispatch<React.SetStateAction<Filters>>
            }
            visibleFilters={visibleFilters}
          />
        }
        filtersRight={
          <SearchBar
            size="sm"
            className="h-fit w-64"
            placeholder="Search assets..."
            value={filters.search ?? ''}
            onChange={handleSearchChange}
          />
        }
        selectionBar={
          <TableSelectionBar
            embedded={true}
            alwaysVisible={true}
            isVisible={selectedAssetKeys.length > 0}
            selectedCount={selectedAssetKeys.length}
            totalCount={totalAssetCount}
            hasNextPage={hasNextPage}
            resourceLabel="assets"
            emptySelectionMessage="Select assets to perform bulk actions"
            actions={
              <TableActionsBar
                selectedItems={selectedAssets}
                actions={[
                  buildAnnotateAction({
                    onCommentClick: () => setIsNoteModalOpen(true),
                    onTagClick: () => setIsTagModalOpen(true),
                    onRemoveTagClick: () => setIsRemoveTagModalOpen(true),
                    labels: { comment: 'Add Note' },
                    testId: 'technology-assets-annotate-dropdown',
                  }),
                  buildExportAction({
                    onExportClick: () => setIsExportModalOpen(true),
                    // Selection-gate the export. The drawer's export config is
                    // whole-tenant (createAssetExportConfig([])), so an
                    // unselected export would silently ship every Asset instead
                    // of the technology-scoped rows (ENG-5121). Requiring a
                    // selection keeps the exported items scoped to real,
                    // chosen asset keys.
                    disabled: selectedAssetKeys.length === 0,
                    testId: 'technology-assets-export-button',
                  }),
                ]}
                disabled={selectedAssetKeys.length === 0}
                hideLabel={true}
                testIdPrefix="technology-assets"
              />
            }
          />
        }
      />

      <div className="min-h-0 flex-1">
        {status === 'pending' ? (
          <Skeleton className="m-6 w-[calc(100%-48px)]" />
        ) : (
          <Table<ImpactedAssetPort>
            name="technologyImpactedAssets"
            error={error}
            status={status}
            data={impactedAssetPorts}
            selection={{
              value: selectedKeys,
              onChange: setSelectedKeys,
            }}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            onRowClick={handleRowClick}
            columns={[
              {
                id: '',
                label: 'Status',
                copy: false,
                cell: ({ asset }) => <AssetStatusComponent asset={asset} />,
                ...getColumnWidth('shared', 'status'),
                sorting: isColumnSortable('shared', 'status'),
              },
              {
                id: '',
                label: 'Asset',
                copy: true,
                cell: ({ asset }) => <TechnologyAssetCell asset={asset} />,
                ...getColumnWidth('asset', 'identifier'),
                sorting: isColumnSortable('asset', 'identifier'),
              },
              {
                id: '',
                label: 'Port',
                copy: true,
                cell: data => data.port.port.toString(),
                ...getColumnWidth('shared', 'port'),
                sorting: isColumnSortable('shared', 'port'),
              },
              {
                id: '',
                label: 'Service',
                copy: true,
                cell: data => (
                  <DashedTag
                    className="max-w-full justify-normal space-x-0 truncate overflow-hidden text-center [&>span]:w-full"
                    label={data.port.service || '-'}
                    size="xs"
                  />
                ),
                ...getColumnWidth('shared', 'service'),
                sorting: isColumnSortable('shared', 'service'),
              },
              {
                id: 'visited',
                label: 'Last Seen',
                copy: true,
                cell: 'date',
                ...getColumnWidth('shared', 'visited'),
                sorting: isColumnSortable('shared', 'visited'),
              },
            ]}
            noData={noDataConfig}
          />
        )}
      </div>

      {/* Modals */}
      <NoteModal
        open={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSubmit={handleNoteSubmit}
        resourceType="Asset"
        mode="simple"
        selectedCount={selectedAssetKeys.length}
        presentation="modal"
      />

      <TagModal
        assetKeys={selectedAssetKeys}
        currentTags={[]}
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        onSuccess={handleTagSuccess}
      />

      <RemoveTagModal
        assetKeys={selectedAssetKeys}
        open={isRemoveTagModalOpen}
        onClose={() => setIsRemoveTagModalOpen(false)}
        onSuccess={handleTagSuccess}
        selectionState={
          shouldUseBulk(selectionState) ? selectionState : undefined
        }
        onBulkRequested={tag =>
          untagConfirm.openConfirmation({
            action: 'untag',
            actionName: 'untag',
            params: { tag },
          })
        }
      />
      <BulkConfirmationDialog
        isOpen={untagConfirm.confirmPayload !== null}
        actionName={untagConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="assets"
        isSelectAll={false}
        itemCount={selectedAssetKeys.length}
        isPending={untagConfirm.isPending}
        onCancel={untagConfirm.closeConfirmation}
        onConfirm={untagConfirm.submitConfirmed}
      />

      {/*
        Two-layer export gate (ENG-5121). The action button is already disabled
        at zero selection, but that alone governs only modal *entry*. This second
        layer keeps the modal from ever being open without a live asset selection:
        if a background refetch empties `selectedAssetKeys` while the modal is
        open, `open` flips false and the modal closes — closing the race where the
        modal's default "All data" scope would otherwise fire a whole-tenant
        export. Scoping the non-"selected" export scopes to the technology itself
        (so "All data" stays technology-bounded) is a larger export-scope-model
        change tracked as a follow-up.
      */}
      <UniversalExportModal
        config={assetExportConfig}
        open={isExportModalOpen && selectedAssetKeys.length > 0}
        onClose={() => setIsExportModalOpen(false)}
        selectedRows={selectedAssetKeys}
        onClearSelection={() => {
          clearSelection();
          setIsExportModalOpen(false);
        }}
      />
    </div>
  );
}
